import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box, Typography, TextField, IconButton, Button, Divider,
  Tooltip, Chip, Select, MenuItem,
  FormControl, Drawer, Alert
} from '@mui/material';
import {
  AutoAwesome, Close, Send, ContentCopy, Refresh,
  SmartToy, OpenInNew, AssignmentTurnedInOutlined,
  SearchOutlined, RouteOutlined
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { marked } from 'marked';
import aiApi, { AIResponseDto, AIRagSourceDto } from '../../api/aiApi';
import { useAuthStore } from '../../store/authStore';
import { sanitizeHtml } from '../../utils/htmlSanitizer';
import FileViewer from './FileViewer';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  localFallback?: boolean;
  sources?: AIRagSourceDto[];
}

type AssistantMode = 'chat' | 'generate' | 'search';

interface QuickPrompt {
  label: string;
  prompt: string;
  mode?: AssistantMode;
}

interface PageProfile {
  title: string;
  description: string;
  icon: React.ReactNode;
  prompts: QuickPrompt[];
  actions: { label: string; path: string }[];
}

interface AIAssistantProps {
  open: boolean;
  onClose: () => void;
  context?: string;
  formType?: string;
  onInsertContent?: (content: string) => void;
  documentContent?: string;
}

const GENERAL_PROMPTS: QuickPrompt[] = [
  { label: 'Summarize', prompt: 'Summarize the current page content into key facts, open items, and important dates.' },
  { label: 'Improve', mode: 'generate', prompt: 'Rewrite the selected or visible content in a clear professional healthcare compliance style.' },
  { label: 'Action Items', prompt: 'Extract action items from the current page. Include owner, due date, and priority where visible.' },
  { label: 'Risk Analysis', prompt: 'Identify likely operational, clinical, privacy, and regulatory risks from the current page context.' },
];

const DRAWER_WIDTH = { xs: '100vw', sm: '380px', md: '410px', lg: '430px', xl: '460px' };
const CONTEXT_CHAR_LIMIT = 6500;

const normalizeText = (text: string) => text.replace(/\s+/g, ' ').trim();

export const formatAssistantResponse = (content: string): string => {
  if (!content) return '';
  const sanitized = sanitizeHtml(marked.parse(content, { async: false }) as string);
  const template = document.createElement('template');
  template.innerHTML = sanitized;
  template.content.querySelectorAll('img').forEach(image => image.remove());
  template.content.querySelectorAll('a').forEach(anchor => {
    const href = anchor.getAttribute('href') || '';
    if (!/^\/(?!\/)/.test(href)) {
      anchor.removeAttribute('href');
      anchor.removeAttribute('target');
      anchor.removeAttribute('rel');
    }
  });
  return template.innerHTML;
};

const uniqueRagSources = (sources: AIRagSourceDto[]) => sources.filter((source, index, all) =>
  all.findIndex(candidate => candidate.linkUrl === source.linkUrl && candidate.title === source.title) === index
).slice(0, 6);

const getSelectedText = () => {
  if (typeof window === 'undefined') return '';
  const selection = window.getSelection?.();
  return normalizeText(selection?.toString() || '');
};

const getFieldSnapshot = (root: Element | null) => {
  if (!root) return '';
  const fields = Array.from(root.querySelectorAll('input, textarea, [role="combobox"]'))
    .map((field: Element) => {
      const input = field as HTMLInputElement | HTMLTextAreaElement;
      const label =
        input.getAttribute('aria-label') ||
        input.getAttribute('name') ||
        input.getAttribute('id') ||
        input.closest('.MuiFormControl-root')?.querySelector('label')?.textContent ||
        input.closest('label')?.textContent ||
        '';
      const value = input.value || input.textContent || input.getAttribute('aria-valuetext') || '';
      return value ? `${normalizeText(label || 'Field')}: ${normalizeText(value)}` : '';
    })
    .filter(Boolean)
    .slice(0, 35);

  return fields.length ? `Visible form fields:\n${fields.join('\n')}` : '';
};

const getTableSnapshot = (root: Element | null) => {
  if (!root) return '';
  const rows = Array.from(root.querySelectorAll('table tr, [role="row"]'))
    .map(row => normalizeText(row.textContent || ''))
    .filter(row => row.length > 8)
    .slice(0, 18);

  return rows.length ? `Visible table/list rows:\n${rows.join('\n')}` : '';
};

const buildLocalFallbackResponse = (prompt: string, context: string, mode: AssistantMode, pageTitle: string) => {
  const compactContext = normalizeText(context);
  const sentences = compactContext
    .split(/(?<=[.!?])\s+|(?:\s+-\s+)/)
    .map(s => s.trim())
    .filter(s => s.length > 30 && !s.toLowerCase().startsWith('current route'))
    .slice(0, 8);
  const lowerPrompt = prompt.toLowerCase();
  const riskWords = ['severe', 'significant', 'overdue', 'external agency', 'not acknowledged', 'pending', 'reopened', 'privacy', 'breach'];
  const riskSignals = sentences.filter(s => riskWords.some(word => s.toLowerCase().includes(word))).slice(0, 5);

  if (mode === 'search') {
    return [
      `I could not reach the configured AI service, so I searched the visible ${pageTitle} context locally.`,
      '',
      sentences.length ? sentences.map((s, i) => `${i + 1}. ${s}`).join('\n') : 'No searchable page text was available. Try opening the relevant record or selecting text before searching.',
    ].join('\n');
  }

  if (lowerPrompt.includes('acknowledg')) {
    return [
      'Draft acknowledgement',
      '',
      'Thank you for raising this complaint. We acknowledge receipt and will review the details carefully, including any immediate actions already taken and any impact on the patient or service.',
      '',
      'We will keep the complainant informed of progress, record investigation notes in the complaint file, and escalate if the review identifies significant harm, external agency involvement, or wider service risk.',
    ].join('\n');
  }

  if (lowerPrompt.includes('action') || lowerPrompt.includes('next')) {
    return [
      `Suggested action plan for ${pageTitle}`,
      '',
      '1. Confirm the complaint status, owner, severity, and acknowledgement details are complete.',
      '2. Review the complaint narrative and reason for complaint for any patient safety, privacy, or service quality themes.',
      '3. Record immediate actions already taken and assign follow-up actions with owners and due dates.',
      '4. Check whether external agency reporting, incident linkage, or escalation is required.',
      '5. Capture final outcome, learning actions, and evidence before marking the record completed.',
    ].join('\n');
  }

  if (lowerPrompt.includes('risk') || lowerPrompt.includes('escalat')) {
    return [
      `Risk review for ${pageTitle}`,
      '',
      riskSignals.length
        ? riskSignals.map(signal => `- ${signal}`).join('\n')
        : '- No specific high-risk wording was detected in the visible page text.',
      '- Check severity, external agency involvement, acknowledgement status, repeated themes, and whether an incident should be linked.',
      '- Keep a clear audit trail of decisions, communications, investigation evidence, corrective actions, and closure rationale.',
    ].join('\n');
  }

  if (lowerPrompt.includes('improve') || mode === 'generate') {
    return [
      'Professional draft',
      '',
      'The complaint has been reviewed against the available record details. The response should clearly describe the concern raised, actions taken at the time, investigation findings, any escalation or external reporting considerations, and the agreed resolution or learning actions.',
      '',
      'Please review and adapt this draft against the full record before using it in a formal report or communication.',
    ].join('\n');
  }

  return [
    `Summary of ${pageTitle}`,
    '',
    sentences.length ? sentences.slice(0, 5).map(s => `- ${s}`).join('\n') : '- No visible page content was available to summarize.',
    '',
    'Useful next checks: confirm ownership, dates, status, severity, acknowledgement, required follow-up, and evidence for closure.',
  ].join('\n');
};

const AIAssistant: React.FC<AIAssistantProps> = ({
  open,
  onClose,
  context,
  onInsertContent,
  documentContent,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrgId, selectedSubOrgId } = useAuthStore();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<AssistantMode>('chat');
  const [copied, setCopied] = useState<number | null>(null);
  const [fileSource, setFileSource] = useState<{ url: string; filename: string } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const pageProfile = useMemo<PageProfile>(() => {
    const path = location.pathname || '/';

    if (path.startsWith('/management/complaints')) {
      return {
        title: path.includes('/new') ? 'New Complaint' : path.includes('/view') ? 'Complaint Review' : 'Complaints',
        description: 'Complaint triage, acknowledgement, investigation, escalation, reporting, and closure support.',
        icon: <AssignmentTurnedInOutlined sx={{ fontSize: 42, color: '#D8E6F3', mb: 1.5 }} />,
        prompts: [
          { label: 'Priorities', prompt: 'Review the visible complaints and identify the top priorities, explaining why each needs attention.' },
          { label: 'Acknowledgement', mode: 'generate', prompt: 'Draft a patient-friendly complaint acknowledgement using the visible complaint context. Keep it professional, concise, and empathetic.' },
          { label: 'Next Actions', prompt: 'Create a complaint action plan from the current context. Include owner, timeframe, evidence needed, and closure criteria.' },
          { label: 'Escalation Check', prompt: 'Assess whether escalation, external agency involvement, incident linkage, or additional reporting should be considered.' },
          { label: 'Report Narrative', mode: 'generate', prompt: 'Draft a complaint report narrative covering concern, assessment, actions taken, outcome, and lessons learned.' },
        ],
        actions: [
          { label: 'New Complaint', path: '/management/complaints/new' },
          { label: 'Master Register', path: '/compliance/master-register' },
          { label: 'Risk Register', path: '/compliance/risk-register' },
        ],
      };
    }

    if (path.startsWith('/compliance/risk')) {
      return {
        title: 'Risk Assistant',
        description: 'Risk description, controls, scoring, treatment actions, and register wording.',
        icon: <RouteOutlined sx={{ fontSize: 42, color: '#D8E6F3', mb: 1.5 }} />,
        prompts: [
          { label: 'Control Gaps', prompt: 'Identify control gaps and missing evidence from the current risk context.' },
          { label: 'Treatment Plan', mode: 'generate', prompt: 'Draft a practical risk treatment plan with actions, owners, due dates, and residual-risk checks.' },
          { label: 'Risk Statement', mode: 'generate', prompt: 'Rewrite this as a clear risk statement with cause, event, impact, and controls.' },
        ],
        actions: [
          { label: 'Risk Register', path: '/compliance/risk-register' },
          { label: 'New Risk', path: '/compliance/risk-assessments/new' },
        ],
      };
    }

    if (path.startsWith('/knowledge')) {
      return {
        title: 'Knowledge Assistant',
        description: 'Document search, policy drafting, summaries, and compliance content improvement.',
        icon: <SearchOutlined sx={{ fontSize: 42, color: '#D8E6F3', mb: 1.5 }} />,
        prompts: [
          { label: 'Find Documents', mode: 'search', prompt: 'Find relevant documents for the current topic. Include document URLs when available.' },
          { label: 'Policy Draft', mode: 'generate', prompt: 'Generate a professional healthcare compliance policy section based on the current context.' },
          { label: 'Reading Summary', prompt: 'Summarize this document into obligations, operational steps, and evidence expectations.' },
        ],
        actions: [
          { label: 'RAG Search', path: '/knowledge/ai-search' },
          { label: 'AI Creator', path: '/knowledge/ai-creator' },
          { label: 'Policies', path: '/knowledge/policies' },
        ],
      };
    }

    return {
      title: 'AI Assistant',
      description: 'Page-aware healthcare compliance assistant using visible records, fields, and selected text.',
      icon: <SmartToy sx={{ fontSize: 44, color: '#E0E0E0', mb: 1.5 }} />,
      prompts: GENERAL_PROMPTS,
      actions: [
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Master Register', path: '/compliance/master-register' },
        { label: 'RAG Search', path: '/knowledge/ai-search' },
      ],
    };
  }, [location.pathname]);

  const getActivePageContext = () => {
    const path = location.pathname || '/';
    const pageName = path
      .split('/')
      .filter(Boolean)
      .map(part => part.replace(/-/g, ' '))
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' > ') || 'Dashboard';

    const mainElement = document.querySelector('main');
    const selectedText = getSelectedText();
    const fieldSnapshot = getFieldSnapshot(mainElement);
    const tableSnapshot = getTableSnapshot(mainElement);
    const visibleText = (mainElement?.textContent || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 4500);

    return [
      `Current route: ${path}`,
      `Current page: ${pageName}`,
      selectedText ? `User-selected text:\n${selectedText.slice(0, 1800)}` : '',
      fieldSnapshot,
      tableSnapshot,
      visibleText ? `Visible page content:\n${visibleText}` : '',
      context ? `Provided context:\n${context}` : '',
      documentContent ? `Document content:\n${documentContent.slice(0, 4000)}` : '',
    ].filter(Boolean).join('\n\n').slice(0, CONTEXT_CHAR_LIMIT);
  };

  const contextLabel = useMemo(() => {
    const path = location.pathname || '/';
    return path === '/' ? 'Dashboard' : path.replace(/^\//, '');
  }, [location.pathname]);

  const extractDocLinks = (content: string): { title: string; path: string }[] => {
    const links: { title: string; path: string }[] = [];
    const regex = /URL:\s*(\/knowledge\/[^\s\n|]+)/g;
    let match;
    while ((match = regex.exec(content)) !== null) {
      const path = match[1].trim();
      const titleMatch = content.substring(0, match.index).match(/Document:\s*"([^"]+)"/g);
      const title = titleMatch ? titleMatch[titleMatch.length - 1].replace(/Document:\s*"/, '').replace(/"$/, '') : path;
      if (!links.find(l => l.path === path)) links.push({ title, path });
    }
    return links;
  };

  const openAssistantLink = (path: string, title = 'document') => {
    if (!path) return;
    if (path.startsWith('/api/files/')) {
      setFileSource({ url: path, filename: title });
      return;
    }
    if (path.startsWith('/')) {
      navigate(path);
    } else {
      window.open(path, '_blank', 'noopener,noreferrer');
    }
    onClose();
  };

  const getSystemMessage = (activeMode: AssistantMode) => {
    const base = 'You are a practical AI assistant embedded in Octans GRC, a healthcare governance, risk, compliance, privacy, knowledge, and complaint management system. Use the provided page context, visible fields, table rows, and selected text. Be specific, concise, and operational. Do not invent facts that are not present; label assumptions clearly. For dates, preserve exact dates from context. When making recommendations, include concrete next actions and evidence to record. Format responses as readable Markdown using headings, paragraphs, emphasis, and lists where helpful.';

    if (activeMode === 'search') {
      return `${base} The user is searching app knowledge or records. Return relevant matches from the supplied context, include exact URL paths when present in the context, and tell the user when a full document or source record should be opened for compliance review.`;
    }

    if (activeMode === 'generate') {
      return `${base} Generate polished healthcare compliance content that can be copied into records or reports. Use clear headings and bullets where helpful. Avoid legal certainty; say what should be checked or confirmed.`;
    }

    return base;
  };

  const sendMessage = async (promptText?: string, overrideMode?: AssistantMode) => {
    const text = promptText || input.trim();
    if (!text || loading) return;
    const activeMode = overrideMode || mode;
    const requestContext = getActivePageContext();
    if (overrideMode && overrideMode !== mode) setMode(overrideMode);

    const userMessage: Message = { role: 'user', content: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      let response: AIResponseDto;

      if (activeMode === 'chat' || activeMode === 'search') {
        const res = await aiApi.searchRag({
          query: text,
          organisationId: selectedOrgId,
          subOrganisationId: selectedSubOrgId,
          maxResults: 8,
          includeAnswer: true,
          includeGraphContext: true,
          graphDepth: 2,
        });
        const ragResponse = res.data.data || res.data as any;
        response = ragResponse;

        // RAG owns factual MMC questions. If it has no matching source, use the
        // current page context so record-specific questions remain useful.
        if (activeMode === 'chat' && (!ragResponse.sources || ragResponse.sources.length === 0) && requestContext) {
          const contextualResponse = await aiApi.generateContent({
            prompt: text,
            context: requestContext,
            systemMessage: getSystemMessage(activeMode),
            maxTokens: 1000,
          });
          response = contextualResponse.data.data || contextualResponse.data as any;
        }

        const assistantMessage: Message = {
          role: 'assistant',
          content: response.success
            ? response.content
            : `${buildLocalFallbackResponse(text, requestContext, activeMode, pageProfile.title)}\n\nNote: ${response.error || 'The RAG service did not return content.'}`,
          timestamp: new Date(),
          localFallback: !response.success,
          sources: response === ragResponse ? uniqueRagSources(ragResponse.sources || []) : [],
        };
        setMessages(prev => [...prev, assistantMessage]);
        return;
      } else {
        const res = await aiApi.generateContent({
          prompt: text,
          context: requestContext,
          systemMessage: getSystemMessage(activeMode),
          maxTokens: activeMode === 'generate' ? 1400 : 1000,
        });
        response = res.data.data || res.data as any;
      }

      const assistantMessage: Message = {
        role: 'assistant',
        content: response.success
          ? response.content
          : `${buildLocalFallbackResponse(text, requestContext, activeMode, pageProfile.title)}\n\nNote: ${response.error || 'The AI service did not return generated content.'}`,
        timestamp: new Date(),
        localFallback: !response.success,
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err: any) {
      const backendError = err?.response?.data?.data?.error
        || err?.response?.data?.error
        || err?.response?.data?.message
        || err?.message
        || 'Failed to connect to AI service.';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `${buildLocalFallbackResponse(text, requestContext, activeMode, pageProfile.title)}\n\nNote: ${backendError}`,
        timestamp: new Date(),
        localFallback: true,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (content: string, index: number) => {
    navigator.clipboard.writeText(content);
    setCopied(index);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleInsert = (content: string) => {
    if (onInsertContent) onInsertContent(content);
  };

  const clearMessages = () => setMessages([]);
  const promptChips = pageProfile.prompts.length ? pageProfile.prompts : GENERAL_PROMPTS;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      PaperProps={{ 'aria-label': 'AI Assistant panel' }}
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          top: 0,
          height: '100%',
          border: 'none',
          borderLeft: '1px solid',
          borderColor: 'divider',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2,
        py: 1.5,
        bgcolor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexShrink: 0,
      }}>
        <AutoAwesome sx={{ color: 'white', fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, flex: 1, fontSize: 14 }}>
          {pageProfile.title}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <Select
            value={mode}
            onChange={e => setMode(e.target.value as any)}
            inputProps={{ 'aria-label': 'AI Assistant mode' }}
            sx={{
              color: 'white',
              fontSize: 12,
              height: 28,
              '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
              '.MuiSvgIcon-root': { color: 'white' },
              '.MuiSelect-select': { py: '4px' },
            }}
          >
            <MenuItem value="chat">Chat</MenuItem>
            <MenuItem value="generate">Generate</MenuItem>
            <MenuItem value="search">Search</MenuItem>
          </Select>
        </FormControl>
        {messages.length > 0 && (
          <Tooltip title="Clear chat">
            <IconButton size="small" onClick={clearMessages} sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.15)' } }}>
            <Close sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ px: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Context: {contextLabel}
        </Typography>
      </Box>

      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75, lineHeight: 1.35 }}>
          {pageProfile.description}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
          {promptChips.slice(0, 5).map(qp => (
            <Chip
              key={qp.label}
              label={qp.label}
              size="small"
              onClick={() => sendMessage(qp.prompt, qp.mode)}
              sx={{
                cursor: 'pointer',
                fontSize: 11,
                height: 24,
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'primary.main', color: 'white', borderColor: 'primary.main' },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: theme => theme.palette.mode === 'dark' ? '#081525' : '#fafafa' }}>
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {pageProfile.icon}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
              {mode === 'search' ? 'Search your documents' :
               mode === 'generate' ? 'Generate GRC content' :
               pageProfile.title}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 2.5 }}>
              {mode === 'search' ? 'Ask questions about your documents and records' :
               mode === 'generate' ? 'Generate professional compliance content' :
               'Ask about the current page, selected text, visible records, or compliance next steps'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
              {promptChips.map(qp => (
                <Chip
                  key={qp.label}
                  label={qp.label}
                  size="small"
                  onClick={() => sendMessage(qp.prompt, qp.mode)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: 11,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                />
              ))}
            </Box>
            {pageProfile.actions.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
                {pageProfile.actions.map(action => (
                  <Chip
                    key={action.path}
                    label={action.label}
                    size="small"
                    variant="outlined"
                    icon={<OpenInNew sx={{ fontSize: '12px !important' }} />}
                    onClick={() => { navigate(action.path); onClose(); }}
                    sx={{ cursor: 'pointer', fontSize: 11, bgcolor: 'background.paper' }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {messages.map((msg, i) => (
          <Box
            key={i}
            sx={{
              mb: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            {msg.role === 'assistant' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <AutoAwesome sx={{ fontSize: 12, color: '#9E9E9E' }} />
                <Typography variant="caption" sx={{ color: '#9E9E9E', fontSize: 11 }}>AI Assistant</Typography>
              </Box>
            )}
            <Box
              onClick={msg.role === 'assistant' ? (event: React.MouseEvent<HTMLDivElement>) => {
                const anchor = (event.target as HTMLElement).closest('a');
                const href = anchor?.getAttribute('href');
                if (!href) return;
                event.preventDefault();
                openAssistantLink(href, anchor?.textContent || 'document');
              } : undefined}
              sx={{
                maxWidth: '88%',
                px: 1.5,
                py: 1,
                borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '2px 12px 12px 12px',
                bgcolor: msg.role === 'user' ? 'primary.main' : 'background.paper',
                color: msg.role === 'user' ? 'white' : 'text.primary',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: msg.role === 'assistant' ? '1px solid' : 'none',
                borderColor: 'divider',
                fontSize: 13,
                lineHeight: 1.6,
                whiteSpace: msg.role === 'user' ? 'pre-wrap' : 'normal',
                wordBreak: 'break-word',
                ...(msg.role === 'assistant' ? {
                  '& p': { my: 0.75 },
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    mt: 1.25,
                    mb: 0.6,
                    lineHeight: 1.3,
                    fontWeight: 700,
                  },
                  '& h1': { fontSize: '1.15rem' },
                  '& h2': { fontSize: '1.08rem' },
                  '& h3, & h4, & h5, & h6': { fontSize: '1rem' },
                  '& ul, & ol': { my: 0.75, pl: 2.5 },
                  '& li': { mb: 0.35 },
                  '& a': { color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' },
                  '& blockquote': {
                    my: 1,
                    mx: 0,
                    pl: 1.25,
                    borderLeft: '3px solid',
                    borderColor: 'divider',
                    color: 'text.secondary',
                  },
                  '& code': { px: 0.35, py: 0.1, borderRadius: 0.5, bgcolor: 'action.hover', fontSize: '0.8rem' },
                  '& pre': { my: 1, p: 1, overflowX: 'auto', borderRadius: 1, bgcolor: 'action.hover', whiteSpace: 'pre' },
                  '& pre code': { p: 0, bgcolor: 'transparent' },
                  '& table': { display: 'block', width: '100%', overflowX: 'auto', borderCollapse: 'collapse', my: 1 },
                  '& th, & td': { border: '1px solid', borderColor: 'divider', px: 0.75, py: 0.5, textAlign: 'left' },
                } : {}),
              }}
            >
              {msg.role === 'assistant'
                ? <Box sx={{ '& > :first-child': { mt: 0 }, '& > :last-child': { mb: 0 } }} dangerouslySetInnerHTML={{ __html: formatAssistantResponse(msg.content) }} />
                : msg.content}
            </Box>
            {msg.localFallback && (
              <Alert severity="info" sx={{ maxWidth: '88%', mt: 0.75, py: 0, fontSize: 11 }}>
                Local fallback used because the AI service did not return a response.
              </Alert>
            )}
            {msg.role === 'assistant' && (() => {
              const docLinks = extractDocLinks(msg.content);
              return (
                <>
                  {docLinks.length > 0 && (
                    <Box sx={{ maxWidth: '88%', mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {docLinks.map((link, li) => (
                        <Chip
                          key={li}
                          label={link.title}
                          size="small"
                          icon={<OpenInNew sx={{ fontSize: '12px !important' }} />}
                          onClick={() => openAssistantLink(link.path, link.title)}
                          sx={{ cursor: 'pointer', fontSize: 11, bgcolor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', '&:hover': { bgcolor: '#DBEAFE' } }}
                        />
                      ))}
                    </Box>
                  )}
                  {msg.sources && msg.sources.length > 0 && (
                    <Box sx={{ maxWidth: '88%', mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {uniqueRagSources(msg.sources).map(source => (
                        <Chip
                          key={source.chunkId}
                          label={`${source.title}${source.versionNumber ? ` v${source.versionNumber}` : ''}`}
                          size="small"
                          icon={<OpenInNew sx={{ fontSize: '12px !important' }} />}
                          onClick={() => openAssistantLink(source.linkUrl, source.title)}
                          sx={{ cursor: source.linkUrl ? 'pointer' : 'default', fontSize: 11, bgcolor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', '&:hover': { bgcolor: '#DBEAFE' } }}
                        />
                      ))}
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    <Tooltip title={copied === i ? 'Copied!' : 'Copy'}>
                      <IconButton size="small" onClick={() => handleCopy(msg.content, i)} sx={{ color: '#9E9E9E', '&:hover': { color: '#424242' } }}>
                        <ContentCopy sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                    {onInsertContent && (
                      <Button size="small" sx={{ fontSize: 11, py: 0, color: '#424242' }} onClick={() => handleInsert(msg.content)}>
                        Insert
                      </Button>
                    )}
                  </Box>
                </>
              );
            })()}
          </Box>
        ))}

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center', px: 1.5, py: 1, bgcolor: 'background.paper', borderRadius: '2px 12px 12px 12px', border: '1px solid', borderColor: 'divider', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              {[0, 1, 2].map(d => (
                <Box key={d} sx={{
                  width: 6, height: 6, borderRadius: '50%', bgcolor: '#9E9E9E',
                  animation: 'bounce 1.2s infinite',
                  animationDelay: `${d * 0.2}s`,
                  '@keyframes bounce': {
                    '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
                    '40%': { transform: 'scale(1)', opacity: 1 },
                  },
                }} />
              ))}
            </Box>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Divider />
      <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'flex-end', bgcolor: 'background.paper', flexShrink: 0 }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder={mode === 'search' ? 'Search documents...' : 'Ask AI...'}
          aria-label={mode === 'search' ? 'Search documents with AI' : 'Ask AI Assistant'}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          sx={{
            '& .MuiInputBase-root': { fontSize: 13, borderRadius: '8px' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' },
          }}
        />
        <IconButton
          onClick={() => sendMessage()}
          disabled={!input.trim() || loading}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: '8px',
            width: 36,
            height: 36,
            flexShrink: 0,
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': { bgcolor: '#E0E0E0', color: '#9E9E9E' },
          }}
          size="small"
        >
          <Send sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      {fileSource && (
        <FileViewer
          url={fileSource.url}
          filename={fileSource.filename}
          defaultFullscreen
          open
          hideTrigger
          onClose={() => setFileSource(null)}
        />
      )}
    </Drawer>
  );
};

export default AIAssistant;
